import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
	Building2,
	Briefcase,
	Globe,
	Linkedin,
	StickyNote,
	Mail,
	Phone,
	CheckSquare,
	CalendarDays,
	Sparkles,
	Loader2,
	Pen,
	ChevronDown,
	ChevronRight,
	User,
	ExternalLink,
	X,
	MessageSquare,
	Send,
	Trash2,
	UserPlus,
	Download,
	Link as LinkIcon,
	Copy,
	Check
} from 'lucide-react';
import {
	Button,
	Input,
	Label,
	Textarea,
	Badge,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	useToast
} from '@hiring-intelligence/ui';
import { companyApi, crmDealsApi } from '@/services/api';
import { DEAL_STAGE_OPTIONS, formatDisplayNumber, CRM_MAIN_INDUSTRIES, CRM_INDUSTRY_GROUPS } from '@hiring-intelligence/shared';
import { format } from 'date-fns';
import { ActivityLog, type ActivityLogHandle } from '@/components/crm/ActivityLog';
import {
	CrmDetailLayout,
	IdentityHeader,
	QuickActionsBar,
	defaultMoreMenu,
	AboutCard,
	EditableTextField,
	EditableTextarea,
	EditableSelectField,
	ReadOnlyField,
	DataHighlights,
	AssociationCard,
	AssociationItem,
	ContactPicker
} from '@/components/crm/layout';

const hrStatusVariant: Record<string, any> = {
	draft: 'secondary', gathering_criteria: 'secondary', criteria_complete: 'default',
	jd_generated: 'default', pending_feedback: 'default', completed: 'success', cancelled: 'destructive'
};
const candidateStatusVariant: Record<string, any> = {
	sourced: 'secondary', contacted: 'default', interviewing: 'default', hired: 'success', rejected: 'destructive'
};
const formatStatus = (status: string) => status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const initials = (n?: string | null) => (n ? n.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') : '?');
const dateFmt = (d?: string | Date | null) => { try { return d ? format(new Date(d), 'd MMM yyyy, HH:mm') : null; } catch { return null; } };

const NOT_SET = '__none__';
function asSelectOptions<T extends { value: string; label: string }>(opts: readonly T[], notSetLabel = 'Not set') {
	return [{ value: NOT_SET, label: notSetLabel }, ...opts];
}

export default function CompanyDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const activityRef = useRef<HTMLDivElement>(null);
	const activityLogRef = useRef<ActivityLogHandle>(null);

	const [expandedHR, setExpandedHR] = useState<Set<string>>(new Set());
	const [showContextInput, setShowContextInput] = useState(false);
	const [regenerateContext, setRegenerateContext] = useState('');
	const [contextInitialized, setContextInitialized] = useState(false);

	// PIC management state
	const [isPicDialogOpen, setIsPicDialogOpen] = useState(false);
	const [picDraft, setPicDraft] = useState<{ id?: string; name: string; email: string; link?: boolean }[]>([]);
	const [originalPics, setOriginalPics] = useState<{ id: string; name: string; email: string }[]>([]);

	// Link generation state
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [linkCvType, setLinkCvType] = useState<'blind' | 'standard'>('blind');
	const [generatedLink, setGeneratedLink] = useState('');
	const [existingLink, setExistingLink] = useState<{ token: string; expiresAt: string } | null>(null);
	const [loadingExisting, setLoadingExisting] = useState(false);
	const [copied, setCopied] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ['company-detail', id],
		queryFn: () => companyApi.get(id!),
		enabled: !!id
	});

	const { data: researchData, isLoading: researchLoading } = useQuery({
		queryKey: ['company-research', id],
		queryFn: () => companyApi.getResearch(id!),
		enabled: !!id,
		refetchInterval: (q) => {
			const r = q.state.data?.data;
			return r && (r.status === 'pending' || r.status === 'processing') ? 5000 : false;
		}
	});

	const { data: contactsData } = useQuery({
		queryKey: ['company-contacts', id],
		queryFn: () => companyApi.listContacts(id!),
		enabled: !!id
	});

	const { data: companyDealsData } = useQuery({
		queryKey: ['crm-deals', { companyId: id }],
		queryFn: () => crmDealsApi.list({ companyId: id!, limit: 100 }),
		enabled: !!id
	});

	const company: any = data?.data;
	const research: any = researchData?.data;
	const pics: any[] = contactsData?.data || [];
	const companyDeals: any[] = companyDealsData?.data || [];
	const hiringRequests: any[] = company?.hiringRequests || [];
	const dealStageLabel = (v?: string | null) => DEAL_STAGE_OPTIONS.find((o) => o.value === v)?.label || v || '';
	const subIndustryOptions = company?.mainIndustry ? (CRM_INDUSTRY_GROUPS[company.mainIndustry] || []).map((s: string) => ({ value: s, label: s })) : [];

	const updateMutation = useMutation({
		mutationFn: (patch: Record<string, any>) => companyApi.update(id!, patch),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['company-detail', id] });
			queryClient.invalidateQueries({ queryKey: ['companies'] });
			toast({ title: 'Saved' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const regenerateMutation = useMutation({
		mutationFn: (ctx?: string) => companyApi.generateResearch(id!, ctx),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['company-research', id] });
			setShowContextInput(false);
			toast({ title: 'Research generation started' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const downloadPdfMutation = useMutation({
		mutationFn: () => companyApi.downloadResearchPDF(id!),
		onSuccess: ({ blob, filename }: any) => {
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url; a.download = filename || 'company-research.pdf'; a.click();
			URL.revokeObjectURL(url);
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const generateLinkMutation = useMutation({
		mutationFn: () => companyApi.generateCompanyLink(id!, linkCvType),
		onSuccess: (result: any) => {
			const url = `${process.env.PORTAL_URL}/hm/${result.data.token}`;
			setGeneratedLink(url);
			toast({ title: 'Link generated' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	const picSyncMutation = useMutation({
		mutationFn: async () => {
			// Keep rows that have an id (existing PIC or a picked contact) or a complete free-text entry.
			const current = picDraft.map((p) => ({ ...p, name: p.name.trim(), email: p.email.trim() })).filter((p) => p.id || (p.name && p.email));
			for (const orig of originalPics) {
				if (!current.find((p) => p.id === orig.id)) await companyApi.removeContact(id!, orig.id);
			}
			for (const p of current) {
				if (p.link && !originalPics.find((o) => o.id === p.id)) {
					// Newly picked existing contact → link it as this company's PIC.
					await companyApi.addContact(id!, { contactId: p.id! });
				} else if (!p.id) {
					await companyApi.addContact(id!, { name: p.name, email: p.email });
				} else {
					const orig = originalPics.find((o) => o.id === p.id);
					if (orig && (orig.name !== p.name || orig.email !== p.email)) {
						await companyApi.updateContact(id!, p.id, { name: p.name, email: p.email });
					}
				}
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['company-contacts', id] });
			setIsPicDialogOpen(false);
			toast({ title: 'PICs updated' });
		},
		onError: (e: Error) => toast({ title: 'Error', description: e.message, variant: 'destructive' })
	});

	useEffect(() => {
		if (research?.additionalContext && !contextInitialized) {
			setRegenerateContext(research.additionalContext);
			setContextInitialized(true);
		}
	}, [research?.additionalContext, contextInitialized]);

	const save = (key: string) => (raw: string) => updateMutation.mutate({ [key]: raw === NOT_SET || raw.trim() === '' ? null : raw });
	const saveIndustryPair = (main: string) => updateMutation.mutate({ mainIndustry: main === NOT_SET ? null : main, subIndustry: null });
	const toggleHR = (hrId: string) => setExpandedHR((prev) => { const n = new Set(prev); n.has(hrId) ? n.delete(hrId) : n.add(hrId); return n; });
	const scrollToActivity = () => activityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	const startActivity = (type: string) => { activityLogRef.current?.startActivity(type); scrollToActivity(); };

	const openPicDialog = () => {
		const seeds = pics.map((p) => ({ id: p.id, name: p.name || '', email: p.email || '' }));
		setOriginalPics(seeds.filter((p) => p.id) as any);
		setPicDraft(seeds);
		setIsPicDialogOpen(true);
	};

	const openLinkDialog = async () => {
		setLinkDialogOpen(true);
		setGeneratedLink('');
		setExistingLink(null);
		setCopied(false);
		setLoadingExisting(true);
		try {
			const result = await companyApi.listCompanyLinks(id!);
			const active = result.data?.find((l: any) => new Date(l.expiresAt) > new Date());
			if (active) setExistingLink({ token: active.token, expiresAt: active.expiresAt });
		} catch {} finally { setLoadingExisting(false); }
	};

	const handleCopy = async (link: string) => {
		await (globalThis as any).navigator.clipboard.writeText(link);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
		toast({ title: 'Copied' });
	};

	if (isLoading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
	if (!company) return (
		<div className="p-6">
			<Link to="/companies" className="text-sm text-primary hover:underline">← Companies</Link>
			<p className="mt-6 text-center text-muted-foreground">Company not found.</p>
		</div>
	);

	const totalCandidates = hiringRequests.reduce((s: number, hr: any) => s + (hr.sourcedCandidates?.length || 0), 0);
	const hiredCount = hiringRequests.reduce((s: number, hr: any) => s + (hr.sourcedCandidates?.filter((c: any) => c.status === 'hired').length || 0), 0);
	const activeHRs = hiringRequests.filter((hr: any) => hr.status !== 'completed' && hr.status !== 'cancelled').length;

	const left = (
		<>
			<IdentityHeader
				backTo="/companies"
				backLabel="Companies"
				avatar={
					<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary font-semibold text-base">
						<Building2 className="h-6 w-6" />
					</div>
				}
				title={company.name}
				subtitle={
					<span>
						{company.industry || <span className="italic">Add industry</span>}
						{company.sizeRange && <span> · {company.sizeRange} employees</span>}
					</span>
				}
				metaRow={
					company.website && (
						<a href={company.website.startsWith('http') ? company.website : `https://${company.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
							{company.website.replace(/^https?:\/\//, '')}
						</a>
					)
				}
				actions={[
					{ label: 'Manage PICs', icon: <UserPlus className="h-4 w-4" />, onClick: openPicDialog },
					{ label: 'Generate client link', icon: <LinkIcon className="h-4 w-4" />, onClick: openLinkDialog }
				]}
			/>

			<QuickActionsBar
				actions={[
					{ label: 'Note', icon: StickyNote, onClick: () => startActivity('note') },
					{ label: 'Email', icon: Mail, onClick: () => startActivity('email') },
					{ label: 'Call', icon: Phone, onClick: () => startActivity('call') },
					{ label: 'Task', icon: CheckSquare, onClick: () => startActivity('task') },
					{ label: 'Meeting', icon: CalendarDays, onClick: () => startActivity('meeting') }
				]}
				moreMenu={defaultMoreMenu({
					onLogSMS: () => startActivity('sms'), onLogLinkedIn: () => startActivity('linkedin'), onLogWhatsApp: () => startActivity('whatsapp')
				})}
			/>

			<AboutCard title="About this company">
				<EditableTextField label="Company Name" value={company.name} onSave={save('name')} placeholder="Name" />
				<EditableTextField label="Website" type="url" value={company.website} onSave={save('website')} placeholder="https://example.com" />
				<EditableTextField label="LinkedIn Company Page" type="url" value={company.linkedinUrl} onSave={save('linkedinUrl')} placeholder="https://linkedin.com/company/..." />
				<EditableTextField label="Country / Region" value={company.location} onSave={save('location')} placeholder="e.g. Indonesia" />
				<EditableTextField label="Industry" value={company.industry} onSave={save('industry')} placeholder="e.g. Technology" />
				<EditableSelectField
					label="Main Industry"
					value={company.mainIndustry}
					options={asSelectOptions(CRM_MAIN_INDUSTRIES.map((m) => ({ value: m, label: m })))}
					onSave={saveIndustryPair}
				/>
				<EditableSelectField
					label="Sub-Industry"
					value={company.subIndustry}
					options={asSelectOptions(subIndustryOptions)}
					onSave={save('subIndustry')}
					disabled={!company.mainIndustry}
					placeholder={company.mainIndustry ? 'Select sub-industry' : 'Set main industry first'}
				/>
				<EditableTextField label="Size Range" value={company.sizeRange} onSave={save('sizeRange')} placeholder="e.g. 100-500" />
				<ReadOnlyField label="Hiring activity">
					<span className="text-sm">
						{activeHRs} active · {hiringRequests.length} total · {hiredCount} hired
					</span>
				</ReadOnlyField>
				<EditableTextarea label="Additional context" value={research?.additionalContext} onSave={() => {}} disabled placeholder="Captured when research is generated." />
			</AboutCard>
		</>
	);

	const researchCard = (
		<div className="rounded-lg border bg-card">
			<div className="flex items-center justify-between border-b px-4 py-2.5">
				<div className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Company Research</div>
				<div className="flex items-center gap-2">
					{research?.status === 'completed' && <Badge variant="secondary">v{research.version}</Badge>}
					{research?.status === 'completed' && research.readableContent && (
						<Button variant="outline" size="sm" onClick={() => downloadPdfMutation.mutate()} disabled={downloadPdfMutation.isPending}>
							<Download className="mr-1 h-3.5 w-3.5" /> {downloadPdfMutation.isPending ? 'Preparing…' : 'PDF'}
						</Button>
					)}
					<Button variant="outline" size="sm" onClick={() => setShowContextInput((s) => !s)} disabled={research?.status === 'pending' || research?.status === 'processing' || regenerateMutation.isPending}>
						<MessageSquare className="mr-1 h-3.5 w-3.5" />
						{regenerateMutation.isPending ? 'Regenerating…' : research ? 'Regenerate' : 'Generate'}
					</Button>
				</div>
			</div>
			<div className="px-4 py-3">
				{showContextInput && (
					<div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
						<Textarea
							placeholder="Add context (e.g. focus on recent acquisitions, leadership changes...)"
							value={regenerateContext}
							onChange={(e) => setRegenerateContext(e.target.value)}
							rows={3}
							className="mb-2 text-sm"
						/>
						<div className="flex items-center gap-2">
							<Button size="sm" onClick={() => regenerateMutation.mutate(regenerateContext || undefined)} disabled={regenerateMutation.isPending}>
								<Send className="mr-1 h-3.5 w-3.5" /> {regenerateMutation.isPending ? 'Generating…' : 'Generate with context'}
							</Button>
							<Button variant="ghost" size="sm" onClick={() => regenerateMutation.mutate(undefined)} disabled={regenerateMutation.isPending}>
								Generate without context
							</Button>
							<Button variant="ghost" size="sm" onClick={() => setShowContextInput(false)}><X className="h-3.5 w-3.5" /></Button>
						</div>
					</div>
				)}
				{researchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> :
				 !research ? <p className="text-sm text-muted-foreground">No research yet. Click Generate.</p> :
				 research.status === 'pending' || research.status === 'processing' ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Generating company research…</div>
				 ) : research.status === 'failed' ? <p className="text-sm text-destructive">Research generation failed.</p> :
				 research.readableContent ? (
					<div className="prose prose-sm dark:prose-invert max-w-none max-h-[400px] overflow-y-auto prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:bg-muted prose-th:text-left prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2">
						<ReactMarkdown remarkPlugins={[remarkGfm]}>{research.readableContent}</ReactMarkdown>
					</div>
				 ) : (
					<pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-96">{JSON.stringify(research.content, null, 2)}</pre>
				 )}
			</div>
		</div>
	);

	const hiringRequestsCard = (
		<div className="rounded-lg border bg-card">
			<div className="flex items-center justify-between border-b px-4 py-2.5">
				<div className="text-sm font-semibold">Hiring requests <span className="text-muted-foreground">({hiringRequests.length})</span></div>
				<div className="text-xs text-muted-foreground">{formatDisplayNumber(totalCandidates)} candidates · {hiredCount} hired</div>
			</div>
			{hiringRequests.length === 0 ? (
				<p className="text-sm text-muted-foreground text-center py-8">No hiring requests yet.</p>
			) : (
				<div className="divide-y">
					{hiringRequests.map((hr: any) => {
						const candidates = hr.sourcedCandidates || [];
						const expanded = expandedHR.has(hr.id);
						const counts = candidates.reduce((acc: Record<string, number>, c: any) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {} as Record<string, number>);
						return (
							<div key={hr.id} className="px-4 py-2.5">
								<div className="flex items-center justify-between gap-2">
									<div className="flex items-center gap-2 min-w-0">
										{candidates.length > 0 && (
											<Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => toggleHR(hr.id)}>
												{expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
											</Button>
										)}
										<div className="min-w-0">
											<Link to={`/hiring-requests/${hr.id}`} className="text-sm font-medium text-primary hover:underline truncate block">{hr.roleTitle}</Link>
											<div className="text-xs text-muted-foreground">
												{formatStatus(hr.yearsOfExperience)}{hr.workType ? ` · ${formatStatus(hr.workType)}` : ''}
											</div>
										</div>
									</div>
									<div className="flex flex-wrap items-center gap-1 shrink-0">
										{Object.entries(counts).map(([s, c]) => (
											<Badge key={s} variant={candidateStatusVariant[s] || 'secondary'} className="text-[10px]">{c as number} {formatStatus(s)}</Badge>
										))}
										<Badge variant={hrStatusVariant[hr.status] || 'secondary'}>{formatStatus(hr.status)}</Badge>
									</div>
								</div>
								{expanded && candidates.length > 0 && (
									<div className="mt-2 ml-7 space-y-1">
										{candidates.map((c: any) => (
											<div key={c.id} className="flex items-center justify-between gap-2 rounded bg-muted/40 px-2.5 py-1.5">
												<div className="flex items-center gap-2 min-w-0">
													<User className="h-3.5 w-3.5 text-muted-foreground" />
													<div className="min-w-0">
														<p className="text-sm font-medium truncate">{c.name}</p>
														<p className="text-xs text-muted-foreground truncate">{[c.currentTitle, c.currentCompany].filter(Boolean).join(' at ')}</p>
													</div>
												</div>
												<div className="flex items-center gap-1.5 shrink-0">
													{c.linkedinUrl && <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>}
													<Badge variant={candidateStatusVariant[c.status] || 'secondary'} className="text-[10px]">{formatStatus(c.status)}</Badge>
												</div>
											</div>
										))}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);

	const overviewBody = (
		<>
			<DataHighlights items={[
				{ label: 'Create Date', value: dateFmt(company.createdAt) },
				{ label: 'Lifecycle Stage', value: hiringRequests.length > 0 ? 'Customer' : 'Opportunity' },
				{ label: 'Last Activity Date', value: dateFmt(company.updatedAt) }
			]} />
			{researchCard}
			{hiringRequestsCard}
			<div ref={activityRef} className="rounded-lg border bg-card">
				<div className="border-b px-4 py-2.5 text-sm font-semibold">Recent activities</div>
				<div className="px-4 py-3"><ActivityLog ref={activityLogRef} companyId={company.id} /></div>
			</div>
		</>
	);

	const right = (
		<>
			<AssociationCard
				title="Contacts (PICs)"
				count={pics.length}
				onAdd={openPicDialog}
				icon={<UserPlus className="h-3.5 w-3.5" />}
				emptyMessage="No contacts yet. Add a PIC to enable client portal access."
			>
				{pics.length > 0 && pics.map((p: any) => (
					<AssociationItem
						key={p.id}
						href={`/contacts/${p.id}`}
						title={
							<span className="inline-flex items-center gap-1.5">
								<span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary">{initials(p.name)}</span>
								{p.name}
							</span>
						}
						meta={<>{p.email && <div className="truncate">{p.email}</div>}{p.title && <div className="truncate">{p.title}</div>}</>}
					/>
				))}
			</AssociationCard>

			<AssociationCard title="Deals" count={companyDeals.length} icon={<Briefcase className="h-3.5 w-3.5" />} emptyMessage="No deals yet.">
				{companyDeals.length > 0 && companyDeals.map((d: any) => (
					<AssociationItem
						key={d.id}
						href={`/deals/${d.id}`}
						title={d.title}
						meta={<>{d.roleTitle && <div className="truncate">{d.roleTitle}</div>}</>}
						badge={<Badge variant="secondary" className="text-[10px]">{dealStageLabel(d.stage)}</Badge>}
					/>
				))}
			</AssociationCard>

			<AssociationCard title="Attachments" emptyMessage="Files attached to this company's activities will appear here." />

			<AssociationCard title="Invoices" emptyMessage="Create an invoice to bill your customer and track payment." />
		</>
	);

	return (
		<>
			<CrmDetailLayout left={left} center={overviewBody} right={right} />

			{/* PIC management dialog */}
			<Dialog open={isPicDialogOpen} onOpenChange={setIsPicDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Manage PICs</DialogTitle>
						<DialogDescription>Persons in charge for this company. PIC emails grant access to the hiring manager portal.</DialogDescription>
					</DialogHeader>
					<div className="space-y-2 py-2">
						<div className="flex items-center justify-between gap-2 pb-1">
							<span className="text-xs text-muted-foreground">Pick an existing client of this company, or add a new PIC below.</span>
							<ContactPicker
								companyId={id}
								excludeIds={picDraft.map((p) => p.id).filter(Boolean) as string[]}
								label="Select existing"
								onSelect={(c) => setPicDraft((prev) => prev.some((p) => p.id === c.id) ? prev : [...prev, { id: c.id, name: c.name, email: c.email || '', link: true }])}
							/>
						</div>
						{picDraft.map((pic, idx) => (
							<div key={pic.id ?? `new-${idx}`} className="flex items-start gap-2">
								<Input placeholder="Name" value={pic.name} onChange={(e) => setPicDraft((prev) => prev.map((p, i) => i === idx ? { ...p, name: e.target.value } : p))} />
								<Input placeholder="email@example.com" type="email" value={pic.email} onChange={(e) => setPicDraft((prev) => prev.map((p, i) => i === idx ? { ...p, email: e.target.value } : p))} />
								<Button variant="ghost" size="icon" onClick={() => setPicDraft((prev) => prev.filter((_, i) => i !== idx))} className="shrink-0">
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
						))}
						<Button variant="outline" size="sm" className="w-fit" onClick={() => setPicDraft((p) => [...p, { name: '', email: '' }])}>
							<UserPlus className="mr-2 h-4 w-4" /> Add PIC
						</Button>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setIsPicDialogOpen(false)}>Cancel</Button>
						<Button onClick={() => picSyncMutation.mutate()} disabled={picSyncMutation.isPending}>
							{picSyncMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Client link dialog */}
			<Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>{existingLink && !generatedLink ? 'Client Link' : 'Generate Client Link'}</DialogTitle>
						<DialogDescription>
							{existingLink && !generatedLink
								? 'An active link already exists for this company.'
								: 'Create a company-level link for hiring managers to access candidate profiles.'}
						</DialogDescription>
					</DialogHeader>
					{loadingExisting ? (
						<div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
					) : generatedLink || existingLink ? (
						<div className="py-2">
							<div className="flex items-center gap-2 rounded-lg bg-muted p-3">
								<Input value={generatedLink || `${process.env.PORTAL_URL}/hm/${existingLink!.token}`} readOnly className="flex-1 text-xs" />
								<Button size="sm" variant="outline" onClick={() => handleCopy(generatedLink || `${process.env.PORTAL_URL}/hm/${existingLink!.token}`)}>
									{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
								</Button>
							</div>
							{existingLink && !generatedLink && (
								<p className="mt-2 text-xs text-muted-foreground">Expires {format(new Date(existingLink.expiresAt), 'd MMM yyyy')}</p>
							)}
							<DialogFooter className="mt-4"><Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Done</Button></DialogFooter>
						</div>
					) : (
						<div className="grid gap-3 py-2">
							<div className="grid gap-2">
								<Label>CV Display Type</Label>
								<Select value={linkCvType} onValueChange={(v: 'blind' | 'standard') => setLinkCvType(v)}>
									<SelectTrigger><SelectValue /></SelectTrigger>
									<SelectContent>
										<SelectItem value="blind">Blind CV (Anonymized)</SelectItem>
										<SelectItem value="standard">Standard CV (Full)</SelectItem>
									</SelectContent>
								</Select>
							</div>
							<DialogFooter>
								<Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
								<Button onClick={() => generateLinkMutation.mutate()} disabled={generateLinkMutation.isPending}>
									{generateLinkMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Generate
								</Button>
							</DialogFooter>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
}
